import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, BookMarked, MoreHorizontal, X, User, CreditCard, Settings, LogOut } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

/**
 * ReaderMobileBottomNav - Fixed bottom navigation bar for authenticated Reader Mode on mobile
 * Only visible on screens < 768px
 * 
 * Layout: Home | Library | My Library | More
 * More menu expands to show: Profile, Account, Billing, Sign Out
 */
const ReaderMobileBottomNav = () => {
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const isActive = (path) => {
    return location.pathname === path;
  };

  const closeMoreMenu = () => {
    setMoreMenuOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    closeMoreMenu();
    navigate('/login');
  };

  return (
    <>
      {/* Fixed Bottom Navigation Bar - Mobile Only */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-slate-950/95 backdrop-blur-md border-t border-slate-800">
        <div className="flex items-center justify-around h-16 px-2">
          {/* Reader Home - Only show if not on home page */}
          {!isActive("/home") && (
            <Link
              to="/home"
              className="flex flex-col items-center justify-center flex-1 h-full transition-colors text-slate-400 hover:text-slate-200"
            >
              <Home className="w-5 h-5 mb-1" />
              <span className="text-xs font-medium">Reader Home</span>
            </Link>
          )}

          {/* My Library - Only show if not on my library page */}
          {!isActive("/reader/my-library") && (
            <Link
              to="/reader/my-library"
              className="flex flex-col items-center justify-center flex-1 h-full transition-colors text-slate-400 hover:text-slate-200"
            >
              <BookMarked className="w-5 h-5 mb-1" />
              <span className="text-xs font-medium">My Library</span>
            </Link>
          )}

          {/* More - Always show */}
          <button
            onClick={() => setMoreMenuOpen(!moreMenuOpen)}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
              moreMenuOpen
                ? "text-purple-400"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            <MoreHorizontal className="w-5 h-5 mb-1" />
            <span className="text-xs font-medium">More</span>
          </button>
        </div>
      </nav>

      {/* More Menu Panel - Slides up from bottom */}
      {moreMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="md:hidden fixed inset-0 bg-black/60 z-40"
            onClick={closeMoreMenu}
          />

          {/* Menu Panel */}
          <div className="md:hidden fixed bottom-16 left-0 right-0 z-50 bg-slate-900/98 backdrop-blur-lg border-t border-slate-700 rounded-t-2xl shadow-2xl animate-slide-up">
            <div className="p-6">
              {/* Close button */}
              <button
                onClick={closeMoreMenu}
                className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <h3 className="text-lg font-semibold text-white mb-4">Account & Settings</h3>

              <div className="space-y-2">
                {/* Profile */}
                <Link
                  to="/account/profile"
                  onClick={closeMoreMenu}
                  className="flex items-center gap-3 px-4 py-3 text-base font-medium text-slate-200 hover:bg-slate-800 rounded-lg transition-colors"
                >
                  <User className="w-5 h-5" />
                  Profile
                </Link>

                {/* Divider */}
                <div className="border-t border-slate-700 my-3" />

                {/* Sign Out */}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-4 py-3 text-base font-medium text-red-400 hover:bg-slate-800 rounded-lg transition-colors w-full text-left"
                >
                  <LogOut className="w-5 h-5" />
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add animation styles */}
      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default ReaderMobileBottomNav;
